import { doc, getDoc, runTransaction, serverTimestamp, setDoc } from 'firebase/firestore';
import { toSceneNpcPlacement } from './sceneDocumentMapper';
import type { SceneDocument, SceneNpcPlacement } from '../../types/scene';
import { getFirebaseDb } from './client';
import { deleteSceneImageAssets } from '../sceneImageAssets';
import { deleteStoragePrefix } from './browserMedia';
import {
  sanitizeSceneMetadataForUpdate,
  sanitizeSceneNpcPlacement,
  sanitizeWorldAnnotationsForLayout,
  sanitizeWorldLayout,
} from './scenePersistenceHelpers';
import { replaceOwnedLevelDesignAnnotations } from '../levelDesigns';
import type { WorldAnnotation } from '../../types/worldAnnotation';
import {
  SCENES_COLLECTION,
  SCENE_USER_PLACEMENTS_COLLECTION,
  buildSceneUserPlacementsDocId,
} from './sceneTypes';
import type { SceneNpcPlacementUpsertInput } from './sceneTypes';
import { getSceneById } from './sceneReads';

const readEditableOwnedSceneAnnotationsContext = async (
  userId: string,
  sceneId: string,
): Promise<{
  existingAnnotations: WorldAnnotation[];
  levelDesignId: string;
  metadataPatch: Pick<SceneDocument, 'name' | 'status' | 'tags'>;
  sceneRef: ReturnType<typeof doc>;
  worldLayout: SceneDocument['worldLayout'];
}> => {
  const db = getFirebaseDb();
  const sceneRef = doc(db, SCENES_COLLECTION, sceneId);
  const snapshot = await getDoc(sceneRef);
  if (!snapshot.exists()) {
    throw new Error('Scene not found.');
  }

  const current = snapshot.data() as Record<string, unknown>;
  if (current.ownerId !== userId) {
    throw new Error('Scene is not editable by current user.');
  }

  const worldLayout = sanitizeWorldLayout(current.worldLayout as SceneDocument['worldLayout']);
  const existingAnnotations =
    sanitizeWorldAnnotationsForLayout(
      Array.isArray(current.annotations)
        ? (current.annotations as SceneDocument['annotations'])
        : [],
      worldLayout,
    ) ?? [];
  return {
    existingAnnotations,
    levelDesignId: String(current.levelDesignId ?? '').trim(),
    metadataPatch: sanitizeSceneMetadataForUpdate(current),
    sceneRef,
    worldLayout,
  };
};

const persistOwnedSceneAnnotations = async (params: {
  userId: string;
  sceneId: string;
  annotations: WorldAnnotation[];
  context?: {
    existingAnnotations: WorldAnnotation[];
    levelDesignId: string;
    metadataPatch: Pick<SceneDocument, 'name' | 'status' | 'tags'>;
    sceneRef: ReturnType<typeof doc>;
    worldLayout: SceneDocument['worldLayout'];
  };
}): Promise<SceneDocument> => {
  const context =
    params.context ??
    (await readEditableOwnedSceneAnnotationsContext(params.userId, params.sceneId));
  const authoredAnnotations = sanitizeWorldAnnotationsForLayout(
    params.annotations,
    context.worldLayout,
  );
  const mirroredAnnotations = context.levelDesignId
    ? ((
        await replaceOwnedLevelDesignAnnotations(
          params.userId,
          context.levelDesignId,
          authoredAnnotations,
        )
      ).annotations ?? [])
    : authoredAnnotations;

  await setDoc(
    context.sceneRef,
    {
      ...context.metadataPatch,
      annotations: mirroredAnnotations,
      updatedAt: serverTimestamp(),
    },
    { merge: true },
  );

  const updated = await getSceneById(params.sceneId);
  if (!updated) {
    throw new Error('Scene annotations were saved but could not be read back.');
  }
  return updated;
};

export const upsertOwnedSceneNpcPlacement = async (
  userId: string,
  sceneId: string,
  payload: SceneNpcPlacementUpsertInput,
): Promise<SceneDocument> => {
  const db = getFirebaseDb();
  const sceneRef = doc(db, SCENES_COLLECTION, sceneId);
  const overlayRef = doc(
    db,
    SCENE_USER_PLACEMENTS_COLLECTION,
    buildSceneUserPlacementsDocId(userId, sceneId),
  );

  await runTransaction(db, async (transaction) => {
    const snapshot = await transaction.get(sceneRef);
    if (!snapshot.exists()) {
      throw new Error('Scene not found.');
    }
    const current = snapshot.data() as Record<string, unknown>;
    const sceneOwnerId = String(current.ownerId ?? '').trim();
    const sceneStatus = String(current.status ?? '').trim();
    const isOwnedScene = sceneOwnerId === userId;
    const isReadableSharedScene = sceneStatus === 'published' || sceneStatus === 'featured';
    if (!isOwnedScene && !isReadableSharedScene) {
      throw new Error('Scene is not editable by current user.');
    }

    const metadataPatch = isOwnedScene ? sanitizeSceneMetadataForUpdate(current) : null;
    const worldLayout = sanitizeWorldLayout(current.worldLayout as SceneDocument['worldLayout']);

    const existingPlacements = isOwnedScene
      ? Array.isArray(current.npcPlacements)
        ? current.npcPlacements
            .map((entry, index) => toSceneNpcPlacement(entry, index, worldLayout))
            .filter((entry): entry is SceneNpcPlacement => entry !== null)
        : []
      : await transaction.get(overlayRef).then((overlaySnapshot) => {
          if (!overlaySnapshot.exists()) {
            return [];
          }
          const overlayData = overlaySnapshot.data() as Record<string, unknown>;
          return Array.isArray(overlayData.npcPlacements)
            ? overlayData.npcPlacements
                .map((entry, index) => toSceneNpcPlacement(entry, index, worldLayout))
                .filter((entry): entry is SceneNpcPlacement => entry !== null)
            : [];
        });

    const basePlacementId = payload.placementId?.trim()
      ? payload.placementId.trim()
      : `${payload.source}:${payload.sourceNpcId ?? Date.now().toString()}`;

    const normalizedPlacement = sanitizeSceneNpcPlacement(
      {
        placementId: basePlacementId,
        source: payload.source,
        sourceNpcId: payload.sourceNpcId,
        inlineConfig: payload.inlineConfig,
        spawnX: payload.spawnX,
        spawnY: payload.spawnY,
        zone: payload.zone,
        movementConfig: payload.movementConfig,
        waypointPath: payload.waypointPath,
      },
      basePlacementId,
      worldLayout,
    );

    if (
      (normalizedPlacement.source === 'system' || normalizedPlacement.source === 'user') &&
      !normalizedPlacement.sourceNpcId
    ) {
      throw new Error('Referenced NPC placements require sourceNpcId.');
    }
    if (normalizedPlacement.source === 'inline' && !normalizedPlacement.inlineConfig) {
      throw new Error('Inline NPC placements require inlineConfig.');
    }

    const byId = new Map<string, SceneNpcPlacement>();
    existingPlacements.forEach((placement) => {
      byId.set(placement.placementId, placement);
    });
    byId.set(normalizedPlacement.placementId, normalizedPlacement);

    if (isOwnedScene) {
      transaction.update(sceneRef, {
        ...metadataPatch,
        npcPlacements: [...byId.values()],
        updatedAt: serverTimestamp(),
      });
      return;
    }

    transaction.set(
      overlayRef,
      {
        ownerId: userId,
        sceneId,
        npcPlacements: [...byId.values()],
        updatedAt: serverTimestamp(),
      },
      { merge: true },
    );
  });

  const updated = await getSceneById(sceneId, userId);
  if (!updated) {
    throw new Error('Scene placement was saved but could not be read back.');
  }
  return updated;
};

export const replaceOwnedSceneAnnotations = async (
  userId: string,
  sceneId: string,
  annotations: WorldAnnotation[],
): Promise<SceneDocument> =>
  persistOwnedSceneAnnotations({
    userId,
    sceneId,
    annotations,
  });

export const upsertOwnedSceneAnnotation = async (
  userId: string,
  sceneId: string,
  annotation: WorldAnnotation,
): Promise<SceneDocument> => {
  if (!annotation.id?.trim()) {
    throw new Error('Annotation id is required.');
  }
  const context = await readEditableOwnedSceneAnnotationsContext(userId, sceneId);
  const byId = new Map(context.existingAnnotations.map((entry) => [entry.id, entry]));
  byId.set(annotation.id, annotation);
  return persistOwnedSceneAnnotations({
    userId,
    sceneId,
    annotations: [...byId.values()],
    context,
  });
};

export const deleteOwnedSceneAnnotation = async (
  userId: string,
  sceneId: string,
  annotationId: string,
): Promise<SceneDocument> => {
  const context = await readEditableOwnedSceneAnnotationsContext(userId, sceneId);
  const nextAnnotations = context.existingAnnotations.filter((entry) => entry.id !== annotationId);
  return persistOwnedSceneAnnotations({
    userId,
    sceneId,
    annotations: nextAnnotations,
    context,
  });
};

export const deleteOwnedScene = async (userId: string, sceneId: string): Promise<void> => {
  const db = getFirebaseDb();
  const sceneRef = doc(db, SCENES_COLLECTION, sceneId);

  await runTransaction(db, async (transaction) => {
    const snapshot = await transaction.get(sceneRef);
    if (!snapshot.exists()) {
      return;
    }

    const current = snapshot.data() as Record<string, unknown>;
    if (current.ownerId !== userId) {
      throw new Error('Scene is not deletable by current user.');
    }

    transaction.delete(sceneRef);
  });
  await deleteStoragePrefix(`scenes/${sceneId}`);
  // Synchronous in-memory cleanup; safe to call directly.
  deleteSceneImageAssets(sceneId);
};
